import {
  isDefined,
  messageFromError,
  toResponseError,
  type AppId,
  type AppVersionData,
  type Semver,
  type VersionAddInput,
  type VersionAddSuccess,
  type VersionGetInput,
  type VersionGetSuccess,
  type VersionRemoveInput,
  type VersionRemoveSuccess,
  type VersionUpdateInput,
  type VersionUpdateSuccess,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import {
  mutationAdd,
  mutationUpdate,
  type AddHookResponse,
  type GetHookResponse,
  type RemoveHookResponse,
  type UpdateHookResponse,
} from '../hooks'
import { connectAppKey, connectAppListKey, connectVersionKey } from './keys'

export function useConnectVersion(
  appId?: AppId,
  appVersion?: Semver,
  enabled: boolean = true
): GetHookResponse<VersionGetSuccess> {
  const input: VersionGetInput | undefined =
    isDefined(appId) && isDefined(appVersion)
      ? {
          appId,
          appVersion,
        }
      : undefined

  return useTantalimQuery<VersionGetInput | undefined, VersionGetSuccess, VersionGetSuccess>({
    url: '/connect/app/version/get',
    queryKey: connectVersionKey(appId, appVersion),
    input,
    convertResponse: (data) => data,
    options: {
      enabled: isDefined(appId) && isDefined(appVersion) && enabled,
    },
  })
}

export function useAddConnectVersion(): AddHookResponse<VersionAddInput, AppVersionData> {
  const mutation = useTantalimMutation<VersionAddInput, VersionAddSuccess>({
    url: '/connect/app/version/add',
  })
  const queryClient = useQueryClient()

  async function add(input: VersionAddInput): Promise<AppVersionData> {
    try {
      const added = await mutation.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectAppListKey() }),
        queryClient.invalidateQueries({ queryKey: connectAppKey(input.versionData.appId) }),
        queryClient.invalidateQueries({
          queryKey: connectVersionKey(input.versionData.appId, input.versionData.appVersion),
        }),
      ])
      return added.appVersion
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, add)
}

export function useUpdateConnectVersion(): UpdateHookResponse<VersionUpdateInput, AppVersionData> {
  const mutation = useTantalimMutation<VersionUpdateInput, VersionUpdateSuccess>({
    url: '/connect/app/version/update',
  })
  const queryClient = useQueryClient()

  async function update(input: VersionUpdateInput): Promise<AppVersionData> {
    try {
      const updated = await mutation.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectAppListKey() }),
        queryClient.invalidateQueries({ queryKey: connectAppKey(input.appVersion.appId) }),
        queryClient.invalidateQueries({
          queryKey: connectVersionKey(input.appVersion.appId, input.appVersion.appVersion),
        }),
      ])
      return updated.appVersion
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}

export function useRemoveConnectVersion(): RemoveHookResponse<VersionRemoveInput> {
  const mutation = useTantalimMutation<VersionRemoveInput, VersionRemoveSuccess>({
    url: '/connect/app/version/remove',
  })
  const queryClient = useQueryClient()

  async function remove(input: VersionRemoveInput): Promise<void> {
    try {
      await mutation.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectAppListKey() }),
        queryClient.invalidateQueries({ queryKey: connectAppKey(input.appId) }),
        queryClient.invalidateQueries({ queryKey: connectVersionKey(input.appId, input.appVersion) }),
      ])
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return {
    remove,
    isRemoving: mutation.isPending,
    error: messageFromError(mutation.error),
  }
}
